import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Sun, Moon, Command, Bell, User as UserIcon,
  LogOut, KeyRound, Mail, History, Shield, ChevronDown,
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_LABEL } from "@/lib/constants";
import api from "@/lib/api";
import { StagePill } from "@/components/StagePill";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

export function Topbar() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [notifs, setNotifs] = useState({ items: [], count: 0 });
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/notifications").then(({ data }) => setNotifs(data)).catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 1) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data.leads || []);
      } catch { setResults([]); }
    }, 200);
  }, [q, open]);

  if (!user) return null;

  const initials = (user.full_name || user.username || "U").split(" ")
    .map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <header
      data-testid="topbar"
      className="h-14 sticky top-0 z-30 bg-background/85 backdrop-blur border-b border-border flex items-center gap-3 px-4"
      style={{ backgroundColor: "color-mix(in srgb, var(--header-color, transparent) 8%, hsl(var(--background)))" }}
    >
      <button
        onClick={() => setOpen(true)}
        data-testid="global-search-trigger"
        className="flex items-center gap-2 text-sm text-muted-foreground bg-secondary hover:bg-accent border border-border rounded-sm px-3 py-1.5 w-full max-w-md transition-colors"
      >
        <Search className="w-4 h-4" />
        <span className="flex-1 text-left">Search leads, customers…</span>
        <span className="flex items-center gap-1 text-[10px] mono px-1.5 py-0.5 border border-border rounded-sm">
          <Command className="w-3 h-3" /> K
        </span>
      </button>

      <div className="flex-1" />

      {/* Notifications */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            aria-label="Notifications"
            data-testid="notif-trigger"
            className="relative w-9 h-9 flex items-center justify-center rounded-sm hover:bg-secondary transition-colors"
          >
            <Bell className="w-4 h-4" />
            {notifs.count > 0 && (
              <span
                data-testid="notif-badge"
                className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 text-[10px] font-semibold bg-red-500 text-white rounded-full flex items-center justify-center"
              >
                {notifs.count > 9 ? "9+" : notifs.count}
              </span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80">
          <DropdownMenuLabel>Notifications</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {notifs.items.length === 0 ? (
            <div className="px-3 py-6 text-sm text-muted-foreground text-center">All clear.</div>
          ) : (
            <div className="max-h-80 overflow-auto">
              {notifs.items.map((n) => (
                <DropdownMenuItem
                  key={`${n.type}-${n.id}`}
                  onSelect={() => navigate(n.link)}
                  data-testid={`notif-${n.type}-${n.id}`}
                  className="cursor-pointer flex-col items-start py-2"
                >
                  <div className="flex items-center gap-2 w-full">
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      n.severity === "warning" ? "bg-amber-500" :
                      n.severity === "danger" ? "bg-red-500" : "bg-blue-500"}`} />
                    <span className="text-sm font-medium flex-1 truncate">{n.title}</span>
                  </div>
                  {n.subtitle && <div className="text-xs text-muted-foreground pl-3.5">{n.subtitle}</div>}
                </DropdownMenuItem>
              ))}
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Theme */}
      <button
        onClick={toggle}
        aria-label="Toggle theme"
        data-testid="theme-toggle"
        className="w-9 h-9 flex items-center justify-center rounded-sm hover:bg-secondary transition-colors"
      >
        {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>

      {/* Profile dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="profile-trigger"
            className="flex items-center gap-2 pl-3 pr-1.5 py-1 rounded-sm border border-border hover:bg-secondary transition-colors"
          >
            {user.photo_url ? (
              <img
  		src={
   		 user.photo_url?.startsWith("http")
   		   ? user.photo_url
    		  : `${import.meta.env.VITE_API_URL}${user.photo_url}`
  			}
  				alt=""
  				className="w-7 h-7 rounded-sm object-cover"
	/>
            ) : (
              <div className="w-7 h-7 rounded-sm flex items-center justify-center text-[11px] font-semibold text-white"
                style={{ backgroundColor: "var(--theme-color)" }}>
                {initials}
              </div>
            )}
            <div className="hidden sm:block leading-tight text-left">
              <div className="text-sm font-medium">{user.full_name}</div>
              <div className="text-[11px] text-muted-foreground">{ROLE_LABEL[user.role]}</div>
            </div>
            <ChevronDown className="w-3 h-3 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs text-muted-foreground">{user.email || user.username}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => navigate("/profile")} data-testid="menu-profile">
            <UserIcon className="w-4 h-4 mr-2" /> My Profile
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => navigate("/profile?tab=password")} data-testid="menu-password">
            <KeyRound className="w-4 h-4 mr-2" /> Change Password
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => navigate("/profile?tab=email")} data-testid="menu-email">
            <Mail className="w-4 h-4 mr-2" /> Change Email
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => navigate("/profile?tab=history")} data-testid="menu-history">
            <History className="w-4 h-4 mr-2" /> Login History
          </DropdownMenuItem>
          {user.role === "master_admin" && (
            <DropdownMenuItem onSelect={() => navigate("/settings")} data-testid="menu-security">
              <Shield className="w-4 h-4 mr-2" /> Security Settings
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={logout} data-testid="menu-logout" className="text-destructive focus:text-destructive">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Search by lead ID, name, company, email, mobile…"
          value={q}
          onValueChange={setQ}
          data-testid="global-search-input"
        />
        <CommandList>
          <CommandEmpty>No results.</CommandEmpty>
          {results.length > 0 && (
            <CommandGroup heading="Leads">
              {results.map((lead) => (
                <CommandItem
                  key={lead.id}
                  value={`${lead.lead_no} ${lead.name} ${lead.company || ""}`}
                  onSelect={() => { setOpen(false); navigate(`/leads?focus=${lead.id}`); }}
                  data-testid={`search-result-${lead.id}`}
                >
                  <div className="flex items-center justify-between w-full gap-3">
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm truncate">
                        <span className="mono text-muted-foreground">{lead.lead_no}</span> · {lead.name}
                      </span>
                      <span className="text-xs text-muted-foreground truncate">
                        {lead.company || "—"} · {lead.email || lead.mobile || ""}
                      </span>
                    </div>
                    <StagePill stage={lead.stage} />
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </header>
  );
}
